import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { AlertTriangle, Wrench, Send, Loader2, ArrowLeft, Plus } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const STATUS_META = {
  open:        { label: "Open",        cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  in_progress: { label: "In progress", cls: "text-blue-300  bg-blue-500/10  border-blue-500/30" },
  resolved:    { label: "Resolved",    cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
};

export default function PortalTickets() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState({ subject: "", body: "", priority: "normal" });
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/tickets");
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  const submitTicket = async () => {
    if (!draft.subject.trim() || !draft.body.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/portal/tickets", draft);
      toast.success(draft.priority === "urgent" ? "Urgent ticket sent — Patrick is being paged." : "Ticket submitted — we'll respond shortly.");
      setComposing(false);
      setDraft({ subject: "", body: "", priority: "normal" });
      setActive(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit ticket");
    } finally {
      setBusy(false);
    }
  };

  const openTicket = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/portal/tickets/${id}`);
      setActive(data);
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/tickets/${active.id}/reply`, { body: reply.trim() });
      setActive(data);
      setReply("");
      toast.success("Reply sent");
      load();
    } catch (e) {
      toast.error("Could not send reply");
    } finally {
      setBusy(false);
    }
  };

  // -------- New ticket form --------
  if (composing) {
    return (
      <div data-testid="portal-ticket-compose" className="max-w-2xl">
        <button onClick={() => setComposing(false)} className="flex items-center gap-2 text-xs text-white/50 hover:text-white mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <h1 className="text-2xl font-medium text-white mb-2">New support request</h1>
        <p className="text-sm text-white/55 mb-6">Urgent = your live system is down or affecting customers. Normal = everything else.</p>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-5 space-y-4">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 block mb-1.5">Priority</label>
            <div className="flex gap-2">
              {[
                { v: "normal", label: "Normal", icon: Wrench, hint: "Question or non-blocking issue" },
                { v: "urgent", label: "Urgent", icon: AlertTriangle, hint: "Live system affected" },
              ].map(({ v, label, icon: Icon, hint }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, priority: v }))}
                  data-testid={`portal-ticket-priority-${v}`}
                  className={`flex-1 flex flex-col items-start gap-1 p-3 rounded-md border text-left transition-colors ${
                    draft.priority === v
                      ? (v === "urgent" ? "border-red-500/50 bg-red-500/5" : "border-[#FFB000]/40 bg-[#FFB000]/5")
                      : "border-white/10 hover:border-white/20"
                  }`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Icon className={`w-3.5 h-3.5 ${v === "urgent" ? "text-red-400" : "text-[#FFB000]"}`} strokeWidth={2.25} />
                    {label}
                  </div>
                  <div className="text-[11px] text-white/45">{hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 block mb-1.5">Subject</label>
            <input
              data-testid="portal-ticket-subject"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              placeholder="One line — what's going on?"
              maxLength={200}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 outline-none focus:border-[#FFB000]/40 transition-colors"
            />
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 block mb-1.5">Message</label>
            <textarea
              data-testid="portal-ticket-body"
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder="Steps to reproduce, screenshot links, when it started — everything helps."
              rows={6}
              maxLength={5000}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 outline-none focus:border-[#FFB000]/40 transition-colors resize-none"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={submitTicket}
              disabled={busy || !draft.subject.trim() || !draft.body.trim()}
              data-testid="portal-ticket-submit"
              className="bg-[#FFB000] hover:bg-[#FFC133] disabled:opacity-50 text-black font-semibold text-sm px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------- Ticket detail view --------
  if (active) {
    return (
      <div data-testid="portal-ticket-detail" className="max-w-2xl">
        <button onClick={() => setActive(null)} className="flex items-center gap-2 text-xs text-white/50 hover:text-white mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to tickets
        </button>
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-medium tracking-tight text-white">{active.subject}</h1>
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium border ${STATUS_META[active.status]?.cls}`}>
            {STATUS_META[active.status]?.label}
          </span>
        </div>
        <div className="text-[11px] font-mono text-white/40 mb-6">
          {active.priority === "urgent" ? "Urgent" : "Normal"} · opened {active.created_at && formatDistanceToNow(new Date(active.created_at), { addSuffix: true })}
        </div>

        <div className="space-y-3 mb-6">
          {(active.thread || []).map((m) => (
            <div key={m.id}
              className={`p-4 rounded-xl border ${m.author_role === "admin" ? "bg-[#FFB000]/5 border-[#FFB000]/20" : "bg-white/[0.02] border-white/10"}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-white/80">{m.author_role === "admin" ? "Repliant Arc support" : "You"}</div>
                <div className="text-[10px] font-mono text-white/30">
                  {m.created_at && formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                </div>
              </div>
              <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{m.body}</div>
            </div>
          ))}
        </div>

        {active.status !== "resolved" && (
          <div className="bg-[#121214] border border-white/10 rounded-xl p-4">
            <textarea
              data-testid="portal-ticket-reply"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Add a reply…"
              rows={4}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white/90 outline-none focus:border-[#FFB000]/40 transition-colors resize-none"
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={sendReply}
                disabled={busy || !reply.trim()}
                data-testid="portal-ticket-reply-send"
                className="bg-[#FFB000] hover:bg-[#FFC133] disabled:opacity-50 text-black font-semibold text-sm px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // -------- List view --------
  return (
    <div data-testid="portal-tickets-page">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-white/40 mb-2">Support</div>
          <h1 className="text-3xl font-medium tracking-tight text-white">My Tickets</h1>
          <p className="text-sm text-white/50 mt-2 max-w-xl">
            Get help, report issues, request changes. Urgent items reach Patrick within seconds.
          </p>
        </div>
        <button
          onClick={() => setComposing(true)}
          data-testid="portal-tickets-new-btn"
          className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-semibold text-sm px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New request
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-[#121214] border border-white/10 rounded-xl py-16 text-center text-white/40 text-sm" data-testid="portal-tickets-empty">
          No tickets yet. Click <span className="text-[#FFB000]">New request</span> to get help.
        </div>
      ) : (
        <div className="bg-[#121214] border border-white/10 rounded-xl overflow-hidden divide-y divide-white/5">
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => openTicket(t.id)}
              data-testid={`portal-ticket-row-${t.id}`}
              className="w-full text-left px-5 py-3.5 flex items-center gap-3 hover:bg-white/[0.02] transition-colors"
            >
              {t.priority === "urgent"
                ? <AlertTriangle className="w-3.5 h-3.5 text-red-400" strokeWidth={2.25} />
                : <Wrench className="w-3.5 h-3.5 text-white/40" strokeWidth={2} />}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white/90 truncate">{t.subject}</div>
                <div className="text-[11px] text-white/40 mt-0.5 font-mono">
                  {t.updated_at && formatDistanceToNow(new Date(t.updated_at), { addSuffix: true })}
                </div>
              </div>
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${STATUS_META[t.status]?.cls}`}>
                {STATUS_META[t.status]?.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
